import { useUnmount } from 'react-use';
import { devtools } from 'zustand/middleware';

import type { Drug } from '@/api/domains/drugs.types';
import { createStoreWithReset } from '@/shared/lib/zustand';

import type { ReconciliationUnboundBinding } from './useLoadUnboundDrugs';

type ReconciliationBindingStore = {
  selectedUnboundDrug: ReconciliationUnboundBinding | null;
  selectedNomenclatureDrug: Drug | null;
  nomenclatureSearchQuery: string;
  isFilterPanelVisible: boolean;

  isBindDialogOpen: boolean;
  isSkipDialogOpen: boolean;

  actions: {
    setSelectedUnboundDrug: (drug: ReconciliationUnboundBinding) => void;
    resetSelectedUnboundDrug: () => void;

    setSelectedNomenclatureDrug: (drug: Drug) => void;
    resetSelectedNomenclatureDrug: () => void;

    setNomenclatureSearchQuery: (query: string) => void;
    clearNomenclatureSearchQuery: () => void;

    toggleFilterPanel: () => void;

    openBindDialog: () => void;
    closeBindDialog: () => void;

    openSkipDialog: () => void;
    closeSkipDialog: () => void;
  };
};

const [useReconciliationBindingStore, resetReconciliationBindingStore] =
  createStoreWithReset<ReconciliationBindingStore>()(
    devtools(
      set => ({
        selectedUnboundDrug: null,
        selectedNomenclatureDrug: null,
        nomenclatureSearchQuery: '',
        isFilterPanelVisible: false,

        isBindDialogOpen: false,
        isSkipDialogOpen: false,

        actions: {
          setSelectedUnboundDrug: drug => set({ selectedUnboundDrug: drug }, false, 'setSelectedUnboundDrug'),
          resetSelectedUnboundDrug: () => set({ selectedUnboundDrug: null }, false, 'resetSelectedUnboundDrug'),

          setSelectedNomenclatureDrug: drug =>
            set({ selectedNomenclatureDrug: drug }, false, 'setSelectedNomenclatureDrug'),
          resetSelectedNomenclatureDrug: () =>
            set({ selectedNomenclatureDrug: null }, false, 'resetSelectedNomenclatureDrug'),

          setNomenclatureSearchQuery: query =>
            set(
              { nomenclatureSearchQuery: query, selectedNomenclatureDrug: null },
              false,
              'setNomenclatureSearchQuery',
            ),
          clearNomenclatureSearchQuery: () =>
            set({ nomenclatureSearchQuery: '', selectedNomenclatureDrug: null }, false, 'clearNomenclatureSearchQuery'),

          toggleFilterPanel: () =>
            set(state => ({ isFilterPanelVisible: !state.isFilterPanelVisible }), false, 'toggleFilterPanel'),

          openBindDialog: () => set({ isBindDialogOpen: true }, false, 'openBindDialog'),
          closeBindDialog: () => set({ isBindDialogOpen: false }, false, 'closeBindDialog'),

          openSkipDialog: () => set({ isSkipDialogOpen: true }, false, 'openSkipDialog'),
          closeSkipDialog: () => set({ isSkipDialogOpen: false }, false, 'closeSkipDialog'),
        },
      }),
      {
        name: 'reconciliation-binding-store',
        enabled: import.meta.env.DEV,
      },
    ),
  );

export const useSelectedUnboundDrug = () => useReconciliationBindingStore(state => state.selectedUnboundDrug);
export const useSelectedNomenclatureDrug = () => useReconciliationBindingStore(state => state.selectedNomenclatureDrug);
export const useNomenclatureSearchQuery = () => useReconciliationBindingStore(state => state.nomenclatureSearchQuery);
export const useIsFilterPanelVisible = () => useReconciliationBindingStore(state => state.isFilterPanelVisible);
export const useIsBindDialogOpen = () => useReconciliationBindingStore(state => state.isBindDialogOpen);
export const useIsSkipDialogOpen = () => useReconciliationBindingStore(state => state.isSkipDialogOpen);
export const useReconciliationBindingActions = () => useReconciliationBindingStore(state => state.actions);

export const useResetReconciliationBindingStoreOnUnmount = () => useUnmount(resetReconciliationBindingStore);
